/**
 * Data Analysis Executor
 *
 * Handles the 'data-analysis' node execution.
 * Calls the ModernizeIT API Layer /dataanalysis endpoint.
 *
 * API Flow:
 * 1. POST /dataanalysis → returns job_id and results
 * 2. If status is 'running', poll GET /dataanalysis/{job_id}/status
 * 3. GET /dataanalysis/{job_id}/results → retrieve full results
 *
 * This node analyzes COBOL data structures to generate:
 * - Entity-Relationship Diagrams (ERD)
 * - Data Lineage graphs
 * - Copybook dependencies
 * - Type mappings for database design
 *
 * 4-Step Analysis Pipeline:
 * 1. Regex extraction (fast) → data_structures.json
 * 2. AST analysis (structural) → hierarchical_structures.json
 * 3. AI analysis (business context) → ai_data_analysis.json
 * 4. ERD generation (merge) → erd.json, data_lineage.json, copybook_analysis.json
 *
 * Artifacts produced:
 * - data_structures.json
 * - hierarchical_structures.json
 * - ai_data_analysis.json (optional)
 * - erd.json
 * - data_lineage.json
 * - copybook_analysis.json
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the data analysis node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing data analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Build the request body
  // Note: Data Analysis only works with COBOL source (no source_type option)
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id, // REQUIRED 2026-01-14: For AWS credentials lookup
  }

  onProgress(5, 'Submitting data analysis job...')

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/dataanalysis`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Data analysis failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Data analysis complete')
  } else {
    // 4. Worker mode: Poll for completion using shared authenticated poller
    onProgress(15, 'Processing data analysis...')
    const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-95%
        const mappedProgress = 15 + (progress * 0.80)
        onProgress(mappedProgress, message)
      },
      signal
    })

    // Merge completed job data
    Object.assign(jobResponse, completedJob)
  }

  onProgress(100, 'Data analysis complete')

  // 5. Return formatted result with job data
  return formatResult(jobId, jobResponse)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data) {
  return {
    // Job tracking
    jobs: {
      dataanalysis_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Paths
    source_path: data.source_path,
    artifacts_path: data.artifacts_path,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Summary statistics
    summary: data.summary || {},

    // Artifacts produced
    artifacts: {
      json: data.json_artifacts || [],
      data_structures: data.artifacts?.data_structures,
      hierarchical_structures: data.artifacts?.hierarchical_structures,
      ai_data_analysis: data.artifacts?.ai_data_analysis,
      erd: data.artifacts?.erd,
      data_lineage: data.artifacts?.data_lineage,
      copybook_analysis: data.artifacts?.copybook_analysis,
    },

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
